import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Alert,
  Grid,
  Layout,
  message,
  Typography,
  Button,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import CanvasArea from "./CanvasArea";
import LeftSidebar from "./LeftSidebar";
import Toolbar from "./Toolbar";
import ActionBar from "./ActionBar";
import ExportModal from "./ExportModal";
import {
  fetchInvitationTemplates,
  fetchInvitationDesigns,
  fetchInvitationDesignById,
  clearCurrentDesign,
} from "../../redux/invitationSlice";
import { API_BASE_URL } from "../../utils/constants";
import { pickInvitationUploadImageUrl } from "../../utils/invitationUpload";
import { MaxInvitationVideosError } from "./invitationFabricVideo";
import "../../assets/styles/components_styles/InvitationBuilder.css";

const { Content } = Layout;

export default function InvitationBuilderPage() {
  const { id: routeDesignId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const canvasRef = useRef(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [activeObject, setActiveObject] = useState(null);
  const [designName, setDesignName] = useState("Untitled invitation");
  const [templateCategory, setTemplateCategory] = useState("all");
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [savedDesignId, setSavedDesignId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("png");
  const [exportQuality, setExportQuality] = useState(0.92);
  const [saving, setSaving] = useState(false);
  const [canvasUiTick, setCanvasUiTick] = useState(0);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [sessionUploadImages, setSessionUploadImages] = useState([]);
  const [shareCode, setShareCode] = useState(null);
  const loadedDesignRef = useRef(null);

  const templates = useSelector((s) => s.invitation.templates);
  const fetchTemplatesLoading = useSelector(
    (s) => s.invitation.fetchTemplatesLoading
  );
  const drafts = useSelector((s) => s.invitation.designs);
  const fetchDesignsLoading = useSelector(
    (s) => s.invitation.fetchDesignsLoading
  );
  const currentDesign = useSelector((s) => s.invitation.currentDesign);
  const fetchDesignLoading = useSelector(
    (s) => s.invitation.fetchDesignByIdLoading
  );

  const authToken =
    typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  const isLoggedIn = Boolean(authToken);

  const effectiveDesignId = routeDesignId
    ? parseInt(routeDesignId, 10)
    : savedDesignId;

  useEffect(() => {
    setCanvasReady(false);
    loadedDesignRef.current = null;
  }, [routeDesignId]);

  useEffect(() => {
    if (!routeDesignId) {
      setShareCode(null);
    }
  }, [routeDesignId]);

  useEffect(() => {
    if (!routeDesignId || !currentDesign?.id) return;
    const routeId = parseInt(routeDesignId, 10);
    if (Number.isNaN(routeId) || Number(currentDesign.id) !== routeId) return;
    setShareCode(currentDesign.share_code ?? null);
  }, [routeDesignId, currentDesign?.id, currentDesign?.share_code]);

  useEffect(() => {
    dispatch(fetchInvitationTemplates());
    if (routeDesignId) {
      dispatch(fetchInvitationDesignById(routeDesignId));
    } else {
      dispatch(clearCurrentDesign());
      setSavedDesignId(null);
      setAppliedTemplateId(null);
      setDesignName("Untitled invitation");
    }
  }, [dispatch, routeDesignId]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchInvitationDesigns());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn && templateCategory === "drafts") {
      setTemplateCategory("all");
    }
  }, [isLoggedIn, templateCategory]);

  useEffect(() => {
    if (
      fetchDesignLoading ||
      !canvasReady ||
      !routeDesignId ||
      !currentDesign?.canvas_data
    ) {
      return;
    }
    const routeId = parseInt(routeDesignId, 10);
    if (Number.isNaN(routeId) || Number(currentDesign.id) !== routeId) {
      return;
    }
    if (loadedDesignRef.current === currentDesign.id) {
      return;
    }
    (async () => {
      try {
        await canvasRef.current.loadFromJson(currentDesign.canvas_data);
        loadedDesignRef.current = currentDesign.id;
        setDesignName(currentDesign.name || "Untitled invitation");
        setAppliedTemplateId(currentDesign.template_id ?? null);
        setSavedDesignId(currentDesign.id);
      } catch (e) {
        console.error(e);
        message.error("Could not load design onto canvas");
      }
    })();
  }, [canvasReady, routeDesignId, currentDesign, fetchDesignLoading]);

  const handleCanvasReady = useCallback(() => {
    loadedDesignRef.current = null;
    setCanvasReady(true);
  }, []);

  const handleSelectionChange = useCallback((obj) => {
    setActiveObject(obj);
  }, []);

  const handleToolbarUpdate = useCallback(() => {
    setCanvasUiTick((t) => t + 1);
  }, []);

  const handleCanvasChange = useCallback(() => {
    setCanvasUiTick((t) => t + 1);
  }, []);

  const handleOpenDraft = useCallback((design) => {
    if (!design?.id) return;
    window.location.assign(`/invitation-builder/${design.id}`);
  }, []);

  const handleApplyTemplate = async (template) => {
    if (!canvasRef.current) return;
    try {
      await canvasRef.current.loadFromJson(template.canvas_data);
      setAppliedTemplateId(template.id);
      message.success(`Applied “${template.name}”`);
    } catch (e) {
      console.error(e);
      message.error("Could not apply template");
    }
  };

  const handleAddText = (preset) => {
    canvasRef.current?.addText(preset);
  };

  const handleImageUpload = async (base64, ext) => {
    if (!isLoggedIn) {
      message.warning("Please sign in to upload images");
      return;
    }
    setUploadLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/invitation-builder/upload-image`,
        { file: base64, file_extension: ext },
        { headers: { Authorization: authToken } }
      );
      const url = pickInvitationUploadImageUrl(res);
      if (!url) {
        message.error("Upload failed: no image URL in response");
        return;
      }
      try {
        await canvasRef.current?.addImageFromUrl(url);
      } catch (canvasErr) {
        if (canvasErr instanceof MaxInvitationVideosError) {
          message.warning(canvasErr.message);
          setSessionUploadImages((prev) => [
            { url, key: `${Date.now()}-${Math.random().toString(36).slice(2)}` },
            ...prev,
          ]);
          return;
        }
        console.error(canvasErr);
        message.error(
          "Image uploaded but could not be loaded onto the canvas. Check image URL or CORS settings."
        );
        setSessionUploadImages((prev) => [
          { url, key: `${Date.now()}-${Math.random().toString(36).slice(2)}` },
          ...prev,
        ]);
        return;
      }
      setSessionUploadImages((prev) => [
        { url, key: `${Date.now()}-${Math.random().toString(36).slice(2)}` },
        ...prev,
      ]);
      message.success("Image added");
    } catch (e) {
      console.error(e);
      message.error(
        e.response?.data?.detail || e.message || "Upload failed"
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const handleAddSessionImageToCanvas = async (url) => {
    if (!url || !canvasRef.current) return;
    try {
      await canvasRef.current.addImageFromUrl(url);
      message.success("Image added");
    } catch (err) {
      if (err instanceof MaxInvitationVideosError) {
        message.warning(err.message);
        return;
      }
      console.error(err);
      message.error("Could not add image to the canvas");
    }
  };

  /** Persists the canvas as a draft. Used by Save and Share. */
  const persistDraft = async () => {
    if (!isLoggedIn) {
      message.warning("Please sign in to save your design");
      return { ok: false };
    }
    if (!canvasRef.current) return { ok: false };

    const canvas_data = canvasRef.current.getCanvasJson();
    let thumbnail = null;
    try {
      thumbnail = canvasRef.current.exportThumbnailBase64();
    } catch (thumbErr) {
      console.warn(thumbErr);
      message.warning(
        "Saved without preview thumbnail. Reload the page or replace cross-origin images if this persists."
      );
    }
    const body = {
      name: designName || "Untitled invitation",
      canvas_data,
      template_id: appliedTemplateId,
      thumbnail,
      status: "draft",
    };

    const numericId =
      effectiveDesignId && !Number.isNaN(effectiveDesignId)
        ? effectiveDesignId
        : null;

    try {
      if (numericId) {
        const { data: res } = await axios.put(
          `${API_BASE_URL}/invitation-designs/${numericId}`,
          body,
          { headers: { Authorization: authToken } }
        );
        const sc = res?.data?.share_code;
        if (sc) setShareCode(sc);
        dispatch(fetchInvitationDesigns());
        return { ok: true, shareCode: sc };
      }
      const { data } = await axios.post(
        `${API_BASE_URL}/invitation-designs`,
        body,
        { headers: { Authorization: authToken } }
      );
      const newId = data?.data?.id;
      const sc = data?.data?.share_code;
      if (sc) setShareCode(sc);
      if (newId) {
        setSavedDesignId(newId);
        navigate(`/invitation-builder/${newId}`, { replace: true });
      }
      dispatch(fetchInvitationDesigns());
      return { ok: true, shareCode: sc, newId };
    } catch (e) {
      console.error(e);
      message.error(e.response?.data?.detail || "Could not save design");
      return { ok: false };
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await persistDraft();
      if (result.ok) message.success("Saved");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (!isLoggedIn) return;
    setSaving(true);
    try {
      const result = await persistDraft();
      if (!result?.ok) return;
      const code = result.shareCode;
      if (!code) {
        message.error("Share link could not be generated. Try saving again.");
        return;
      }
      const url = `${window.location.origin}/invitation-share/${code}`;
      try {
        await navigator.clipboard.writeText(url);
        message.success("Draft saved. Share link copied to clipboard.");
      } catch {
        message.success("Draft saved.");
        message.info(url);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExportDownload = () => {
    if (!canvasRef.current) return;
    let dataUrl;
    try {
      dataUrl = canvasRef.current.exportDataUrl({
        format: exportFormat,
        quality: exportQuality,
        multiplier: 2,
      });
    } catch (err) {
      console.error(err);
      message.error(
        "Could not export image (canvas may contain blocked cross-origin images). Try removing those images or reload and add them again."
      );
      return;
    }
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${(designName || "invitation").replace(/\s+/g, "-")}.${
      exportFormat === "jpeg" ? "jpg" : "png"
    }`;
    a.click();
    setExportOpen(false);
    message.success("Download started");
  };

  if (isMobile) {
    return (
      <Layout className="invitation-builder invitation-builder--mobile">
        <Content style={{ padding: 24 }}>
          <Alert
            type="warning"
            showIcon
            message="Desktop recommended"
            description="The invitation builder works best on a larger screen. Please open this page on a tablet or desktop."
          />
          <Link to="/">
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              icon={<ArrowLeftOutlined />}
            >
              Back home
            </Button>
          </Link>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="invitation-builder">
      <Helmet>
        <title>Invitation builder | PingVite</title>
      </Helmet>
      <Content>
        <div className="invitation-builder__top">
          <Link to="/" className="invitation-builder__back">
            <ArrowLeftOutlined /> <span>Home</span>
          </Link>
          <ActionBar
            designName={designName}
            onDesignNameChange={setDesignName}
            onSave={handleSave}
            onExport={() => setExportOpen(true)}
            onShare={handleShare}
            saveLoading={saving}
            disabledSave={!isLoggedIn}
            showShare={isLoggedIn}
            shareUrl={
              shareCode
                ? `${window.location.origin}/invitation-share/${shareCode}`
                : null
            }
          />
        </div>

        {!isLoggedIn && (
          <Alert
            className="invitation-builder__auth-hint"
            type="info"
            showIcon
            message={
              <span>
                <Typography.Text strong>Sign in</Typography.Text> to save drafts
                and upload images. You can still edit and export locally.
              </span>
            }
          />
        )}

        <div className="invitation-builder__main">
          <aside className="invitation-builder__aside">
            <LeftSidebar
              templates={templates}
              drafts={drafts}
              templatesLoading={fetchTemplatesLoading}
              draftsLoading={fetchDesignsLoading}
              onApplyTemplate={handleApplyTemplate}
              onOpenDraft={handleOpenDraft}
              templateCategory={templateCategory}
              onTemplateCategoryChange={setTemplateCategory}
              onAddText={handleAddText}
              onImageUploaded={handleImageUpload}
              uploadLoading={uploadLoading}
              sessionUploadImages={sessionUploadImages}
              onAddSessionImageToCanvas={handleAddSessionImageToCanvas}
              isLoggedIn={isLoggedIn}
            />
          </aside>

          <div
            className="invitation-builder__stage"
            data-fabric-ui-tick={canvasUiTick}
          >
            <Toolbar
              activeObject={activeObject}
              onUpdateObject={handleToolbarUpdate}
            />
            <div className="invitation-builder__canvas-outer">
              {fetchDesignLoading && routeDesignId && (
                <div className="invitation-builder__canvas-loading">
                  Loading design…
                </div>
              )}
              <CanvasArea
                key={routeDesignId || "new"}
                ref={canvasRef}
                onSelectionChange={handleSelectionChange}
                onCanvasChange={handleCanvasChange}
                onReady={handleCanvasReady}
              />
            </div>
          </div>
        </div>
      </Content>

      <ExportModal
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        format={exportFormat}
        onFormatChange={setExportFormat}
        quality={exportQuality}
        onQualityChange={setExportQuality}
        onDownload={handleExportDownload}
      />
    </Layout>
  );
}
